import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Typography } from "@mui/material";
import {
  useTreatments,
  useDeleteTreatment,
  useBulkDeleteTreatments,
} from "../api/treatments";
import { useProject } from "../api/project";
import { Treatment, Treatments } from "../types/treatment";
import { ApiResponse } from "../types/api";
import { StrategyContext } from "../contexts/StrategyContext";
import {
  DrawingContext,
  DrawingFeatureType,
  DrawingMode,
} from "../contexts/DrawingContext";
import AddEditTreatmentModal, { TreatmentData } from "./AddEditTreatmentModal";
import TreatmentCostAnalysisModal from "./TreatmentCostAnalysisModal";
import ScenarioSegmentButton from "./ScenarioSegmentButton";
import { useAuth } from "../contexts/AuthContext";
import { isViewOnly } from "../utils/permissions";
import { useUnitSystem } from "../hooks/useUnitSystem";
import { formatArea, formatNumber } from "../utils/units";
import {
  treatmentEffectDate,
  treatmentTypeIcon,
  treatmentYear,
  treatmentYearColor,
} from "../utils/treatmentDisplay";
import {
  FooterLink,
  ScenarioDivider,
  ScenarioEmptyState,
  ScenarioList,
  ScenarioPopover,
  ScenarioRow,
  StatusLine,
} from "./scenarioBar/ScenarioPopover";
import { useUndoableDelete } from "./scenarioBar/useUndoableDelete";
import { UndoToast } from "./scenarioBar/UndoToast";
import { SB, labelSx, tabularSx, typeGrey } from "./scenarioBar/tokens";

type TypeSummary = {
  type: string;
  count: number;
  areaHa: number;
  cost: number;
};

const formatCost = (cost: number) =>
  `$${formatNumber(cost, { maximumFractionDigits: 0 })}`;

/** Area with one decimal for the segment ("103.7 ha"); formatArea keeps the
 *  unit system but rounds to whole figures for imperial. */
const formatAreaShort = (hectares: number, unitSystem: "metric" | "imperial") => {
  if (unitSystem === "metric") {
    return `${formatNumber(hectares, { maximumFractionDigits: 1 })} ha`;
  }
  return formatArea(hectares, unitSystem);
};

const SUMMARY_COLUMNS = "minmax(0, 1fr) 44px 84px 60px";

const Swatch = ({ color, size = 8 }: { color: string; size?: number }) => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{ width: size, height: size, borderRadius: "2px", background: color, flex: "none" }}
  />
);

export default function TreatmentsDropdown() {
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const unitSystem = useUnitSystem();
  const { selectedStrategy } = useContext(StrategyContext);
  const { setDrawingMode, setDrawingFeatureType } = useContext(DrawingContext);
  const { treatments, isLoading, error, refetch } = useTreatments();
  const { project } = useProject();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { projectId } = useParams();
  const projectIdNumber = Number(projectId) || 0;
  const { mutate: deleteTreatment } = useDeleteTreatment(projectIdNumber);
  const { mutate: bulkDelete } = useBulkDeleteTreatments(projectIdNumber);
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [treatmentToEdit, setTreatmentToEdit] = useState<TreatmentData | null>(
    null
  );
  const { pending, scheduleDelete, undo, dismiss } = useUndoableDelete();

  const treatmentsKey = ["strategy", "treatments", selectedStrategy?.id];

  // A pending delete belongs to the strategy it was made in: switching
  // strategies sends it rather than letting it fire against the new one.
  const strategyId = selectedStrategy?.id;
  useEffect(() => {
    dismiss();
  }, [strategyId, dismiss]);

  const features: Treatment[] = useMemo(
    () => treatments?.data?.features ?? [],
    [treatments]
  );

  // Per-type rollup of what the backend already sends with each feature
  // (SizeHa, Cost), so the bar can show totals without another request.
  const summary = useMemo(() => {
    const byType = new Map<string, TypeSummary>();
    let areaHa = 0;
    let cost = 0;
    for (const feature of features) {
      const type = String(feature.properties?.Type || "Other");
      const featureArea = Number(feature.properties?.SizeHa) || 0;
      const featureCost = Number(feature.properties?.Cost) || 0;
      const row = byType.get(type) ?? { type, count: 0, areaHa: 0, cost: 0 };
      row.count += 1;
      row.areaHa += featureArea;
      row.cost += featureCost;
      byType.set(type, row);
      areaHa += featureArea;
      cost += featureCost;
    }
    const rows = Array.from(byType.values()).sort((a, b) =>
      a.type.localeCompare(b.type)
    );
    return { rows, count: features.length, areaHa, cost };
  }, [features]);

  const projectAreaHa = useMemo(() => {
    const kpi = project?.data?.kpis?.find((k) => k.label === "Total area");
    const value = Number(kpi?.value);
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [project]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // "New" starts drawing a treatment area on the map, the same as the Draw
  // button on the Treatments layer; the add modal opens when the shape closes.
  const startDrawing = () => {
    handleClose();
    setDrawingMode(DrawingMode.TREATMENT);
    setDrawingFeatureType(DrawingFeatureType.POLYGON);
  };

  const startImport = () => {
    handleClose();
    setDrawingMode(DrawingMode.TREATMENT);
    setDrawingFeatureType(DrawingFeatureType.UPLOAD);
  };

  const handleEditTreatment = (treatment: Treatment) => {
    setTreatmentToEdit({
      treatmentId: treatment.id,
      name: treatment.properties.Name,
      type: treatment.properties.Type,
      residualFuel: treatment.properties.ResidualFuel,
      startDate: treatment.properties.StartDate,
      endDate: treatment.properties.EndDate,
      vegClass: treatment.properties.VegClass,
      vegDate: treatment.properties.VegDate,
      width: treatment.properties.width,
      costPerUnit: treatment.properties.CostPerUnit,
      elevatedFuelReductionPct: treatment.properties.ElevatedFuelReductionPct,
      surfaceFuelReductionPct: treatment.properties.SurfaceFuelReductionPct,
      removeSlash: treatment.properties.RemoveSlash,
    });
  };

  // Hide the given features at once (the map layer reads the same cache, so
  // the polygons go too), offer Undo for six seconds, then send the delete.
  const hideThenDelete = (
    ids: Set<number>,
    text: string,
    commit: () => void
  ) => {
    const previous = queryClient.getQueryData<ApiResponse<Treatments>>(treatmentsKey);
    if (previous?.data) {
      queryClient.setQueryData(treatmentsKey, {
        ...previous,
        data: {
          ...previous.data,
          features: previous.data.features.filter((f) => !ids.has(f.id)),
        },
      });
    }
    scheduleDelete({
      text,
      commit,
      restore: () => {
        if (previous) queryClient.setQueryData(treatmentsKey, previous);
      },
    });
  };

  const handleDeleteTreatment = (treatment: Treatment) =>
    hideThenDelete(
      new Set([treatment.id]),
      `Deleted “${treatment.properties.Name}”`,
      () => deleteTreatment({ id: treatment.id })
    );

  const handleDeleteSeries = (treatment: Treatment) => {
    const seriesId = treatment.properties.SeriesId;
    const ids = new Set(
      features
        .filter((f) => seriesId != null && f.properties?.SeriesId === seriesId)
        .map((f) => f.id)
    );
    ids.add(treatment.id);
    hideThenDelete(
      ids,
      `Deleted series “${treatment.properties.Name}” (${ids.size})`,
      () => deleteTreatment({ id: treatment.id, deleteSeries: true })
    );
  };

  const handleClearAll = () => {
    if (!features.length) return;
    const ids = features.map((t) => t.id);
    hideThenDelete(
      new Set(ids),
      `Cleared ${ids.length} ${ids.length === 1 ? "treatment" : "treatments"}`,
      () => bulkDelete(ids)
    );
  };

  const summaryValue = isLoading
    ? "Loading…"
    : summary.count === 0
    ? "None applied"
    : `${summary.count} applied`;

  const strategyName = selectedStrategy?.name ?? "this strategy";

  const cellSx = { ...tabularSx, textAlign: "right" as const, color: SB.textTable };
  const headSx = { ...labelSx, textAlign: "right" as const };

  return (
    <div>
      <ScenarioSegmentButton
        label="Treatments"
        icon="shield"
        value={summaryValue}
        meta={summary.count ? formatAreaShort(summary.areaHa, unitSystem) : undefined}
        tooltip={
          summary.count
            ? `${summary.count} treatment${summary.count === 1 ? "" : "s"} in ${strategyName}`
            : "No treatments in this strategy yet"
        }
        open={Boolean(anchorEl)}
        onClick={handleClick}
        disabled={!selectedStrategy}
        maxValueWidth={200}
      />
      <ScenarioPopover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        title="Treatments"
        count={summary.count}
        scope={selectedStrategy ? `in ${selectedStrategy.name}` : undefined}
        onNew={viewOnly ? undefined : startDrawing}
        footer={
          summary.count > 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.25,
                py: 1,
              }}
            >
              <FooterLink
                icon="bar_chart"
                label="Financial analysis"
                disabled={!selectedStrategy}
                onClick={() => {
                  handleClose();
                  setCostModalOpen(true);
                }}
              />
              {!viewOnly && <FooterLink quiet label="Clear all" onClick={handleClearAll} />}
            </Box>
          ) : undefined
        }
      >
        {isLoading && <StatusLine>Loading…</StatusLine>}
        {error && <StatusLine>Error: {error.message}</StatusLine>}

        {!isLoading && !error && summary.count === 0 && (
          <ScenarioEmptyState
            icon="shield"
            title={`No treatments in ${strategyName}`}
            body="Draw a treatment area on the map, or import one from a file."
          >
            {!viewOnly && (
              <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                <FooterLink icon="draw" label="Draw on map" onClick={startDrawing} />
                <FooterLink icon="upload_file" label="Import file" onClick={startImport} />
              </Box>
            )}
          </ScenarioEmptyState>
        )}

        {summary.count > 0 && (
          <>
            {/* Summary: treated area, cost, share bar and per-type table.
                Type is told by grey + label; colour is kept for burn year. */}
            <Box sx={{ px: 2, pt: 1.75, pb: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <Typography component="span" sx={labelSx}>
                    Treated area
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      ...tabularSx,
                      fontSize: 20,
                      lineHeight: "26px",
                      fontWeight: 600,
                      letterSpacing: "-0.01em",
                      color: SB.text,
                    }}
                  >
                    {formatArea(summary.areaHa, unitSystem)}{" "}
                    {projectAreaHa && (
                      <Box
                        component="span"
                        sx={{ fontSize: 12, fontWeight: 500, letterSpacing: 0, color: SB.meta }}
                      >
                        {formatNumber((summary.areaHa / projectAreaHa) * 100, {
                          maximumFractionDigits: 1,
                        })}
                        % of project
                      </Box>
                    )}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", alignItems: "flex-end" }}>
                  <Typography component="span" sx={labelSx}>
                    Est. cost
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      ...tabularSx,
                      fontSize: 20,
                      lineHeight: "26px",
                      fontWeight: 600,
                      letterSpacing: "-0.01em",
                      color: SB.text,
                    }}
                  >
                    {formatCost(summary.cost)}
                  </Typography>
                </Box>
              </Box>

              {summary.areaHa > 0 && (
                <Box
                  role="img"
                  aria-label="Share of treated area by type"
                  sx={{
                    display: "flex",
                    gap: "2px",
                    height: 6,
                    borderRadius: 3,
                    overflow: "hidden",
                    background: "rgba(255,255,255,0.06)",
                  }}
                >
                  {summary.rows.map((row, i) => (
                    <Box
                      key={row.type}
                      sx={{ flex: `${row.areaHa} 1 0`, background: typeGrey(i) }}
                    />
                  ))}
                </Box>
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: SUMMARY_COLUMNS,
                  columnGap: 1,
                  rowGap: 0.75,
                  fontSize: 12,
                  lineHeight: "18px",
                }}
              >
                <Typography component="span" sx={labelSx}>
                  Type
                </Typography>
                <Typography component="span" sx={headSx}>
                  Count
                </Typography>
                <Typography component="span" sx={headSx}>
                  Area
                </Typography>
                <Typography component="span" sx={headSx}>
                  Cost
                </Typography>
                {summary.rows.map((row, i) => (
                  <Box key={row.type} sx={{ display: "contents" }}>
                    <Box
                      component="span"
                      sx={{ display: "flex", alignItems: "center", gap: 1, color: SB.textBody, minWidth: 0 }}
                    >
                      <Swatch color={typeGrey(i)} />
                      <Typography noWrap component="span" sx={{ fontSize: "inherit" }}>
                        {row.type}
                      </Typography>
                    </Box>
                    <Box component="span" sx={cellSx}>
                      {row.count}
                    </Box>
                    <Box component="span" sx={cellSx}>
                      {formatArea(row.areaHa, unitSystem)}
                    </Box>
                    <Box component="span" sx={cellSx}>
                      {formatCost(row.cost)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            <ScenarioDivider />
            <ScenarioList ariaLabel="All treatments" maxHeight={5 * SB.rowHeightTwoLine + 12}>
              {features.map((treatment) => {
                const props = treatment.properties ?? {};
                const type = String(props.Type || "Other");
                const year = treatmentYear(props);
                const seriesSize = Number(props.SeriesSize) || 1;
                const areaHa = Number(props.SizeHa) || 0;
                return (
                  <ScenarioRow
                    key={treatment.id}
                    icon={treatmentTypeIcon(type)}
                    name={props.Name || `Treatment ${treatment.id}`}
                    subline={
                      <>
                        <Typography noWrap component="span" sx={{ fontSize: "inherit", minWidth: 0 }}>
                          {type}
                        </Typography>
                        <Box component="span" sx={{ color: SB.radioOff }}>
                          ·
                        </Box>
                        <Swatch color={treatmentYearColor(treatmentEffectDate(props))} />
                        {year !== null && (
                          <Box component="span" sx={tabularSx}>
                            {year}
                          </Box>
                        )}
                      </>
                    }
                    meta={areaHa ? formatArea(areaHa, unitSystem) : undefined}
                    actions={
                      viewOnly
                        ? []
                        : [
                            {
                              icon: "edit",
                              label: "Edit treatment",
                              onClick: () => handleEditTreatment(treatment),
                            },
                          ]
                    }
                    menuItems={
                      viewOnly
                        ? []
                        : [
                            {
                              icon: "delete",
                              label: "Delete treatment",
                              danger: true,
                              onClick: () => handleDeleteTreatment(treatment),
                            },
                            ...(seriesSize > 1
                              ? [
                                  {
                                    icon: "delete_sweep",
                                    label: `Delete series (${seriesSize})`,
                                    danger: true,
                                    onClick: () => handleDeleteSeries(treatment),
                                  },
                                ]
                              : []),
                          ]
                    }
                  />
                );
              })}
            </ScenarioList>
          </>
        )}
      </ScenarioPopover>
      <UndoToast pending={pending} onUndo={undo} onDismiss={dismiss} />
      <TreatmentCostAnalysisModal
        open={costModalOpen}
        onClose={() => setCostModalOpen(false)}
      />
      {treatmentToEdit && (
        <AddEditTreatmentModal
          mode="edit"
          initialData={treatmentToEdit}
          onClose={() => {
            setTreatmentToEdit(null);
            refetch();
            setAnchorEl(null);
          }}
        />
      )}
    </div>
  );
}
